import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, MapPin } from 'lucide-react';

const Experience = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const experiences = [
    {
      company: 'Nexent Bank N.V. Amsterdam Sucursala București',
      title: 'System DevOps Engineer',
      location: 'Bucharest, Romania',
      period: 'May 2025 - Present',
      current: true,
      responsibilities: [
        'Handling migrations and management implementation on systems and backends',
        'Maintaining OCP, AAP pipelines and workloads',
        'Managing Azure and on-prem infrastructure',
        'Creating logging and monitoring systems using Elasticsearch, Nagios, Ansible, and Prometheus'
      ]
    },
    {
      company: 'Havelsan',
      title: 'DevOps Engineer',
      location: 'Ankara, Türkiye',
      period: 'January 2024 - December 2024',
      responsibilities: [
        'Working with the Software Infrastructures team',
        'Handling Jenkins, Swarm, Rancher, and Kubernetes workloads and infrastructure',
        'Creating CI/CD pipelines with Helm, K8S, and Bash',
        'Designing, implementing, and deploying solutions on modern application development workflows'
      ]
    },
    {
      company: 'Beko',
      title: 'Senior Specialist Engineer / Scrum Master',
      location: 'Ankara, Türkiye',
      period: 'September 2021 - October 2023',
      responsibilities: [
        'Worked with the IoT Team on the Cloud Chapter',
        'Handled IoT backbone/services on AWS, monitoring and adding features to the base infrastructure',
        'Worked with Jenkins, Sonar, and various AWS tools to develop services with Java, Python, and AWS',
        'Infrastructure/deployment planning and user experience processes using Jenkins, Serverless, TeamCity',
        'Served as Scrum Master, focusing on in-company communications and operations'
      ]
    },
    {
      company: 'Ekinoks Software',
      title: 'Systems Specialist',
      location: 'Ankara, Türkiye',
      period: '2020 - 2021',
      responsibilities: [
        'Created CI/CD solutions with GitLab, Docker, Helm, and on-prem Kubernetes',
        'Created systems and analytics to better observe, monitor, and optimize systems',
        'Added features to the base infrastructure with Istio, Helm, Kubernetes, and GitLab Runners'
      ]
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section
      id="experience"
      ref={sectionRef}
      className="section-padding py-20 px-6"
    >
      <div className="max-w-6xl mx-auto">
        <div
          className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <h2 className="section-title text-4xl md:text-5xl font-bold text-white mb-4">
            <span className="text-cyan-400 font-mono text-lg">02.</span> Experience
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 mb-12"></div>

          <div className="relative">
            {/* Timeline line */}
            <div className="absolute left-4 md:left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400 via-blue-500 to-transparent"></div>

            <div className="space-y-8">
              {experiences.map((exp, index) => (
                <div
                  key={index}
                  className="relative pl-12 md:pl-20"
                  style={{
                    animation: `fadeInUp 0.6s ease-out ${index * 0.1}s both`
                  }}
                >
                  {/* Timeline dot */}
                  <div className="absolute left-2 md:left-6 top-2 w-4 h-4 bg-cyan-400 rounded-full border-4 border-slate-900 shadow-lg shadow-cyan-400/50"></div>

                  <Card className="glass-card bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl overflow-hidden hover:border-cyan-400/30 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-400/10">
                    <CardContent className="p-6 md:p-8">
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-start justify-between gap-4">
                          <div>
                            <h3 className="text-2xl font-bold text-white mb-2">
                              {exp.title}
                            </h3>
                            <p className="text-cyan-400 font-semibold text-lg">
                              {exp.company}
                            </p>
                          </div>
                          {exp.current && (
                            <Badge className="bg-cyan-500/20 text-cyan-400 border border-cyan-400/50 px-3 py-1">
                              Current
                            </Badge>
                          )}
                        </div>

                        <div className="flex flex-wrap gap-4 text-gray-400 text-sm">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            <span>{exp.period}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            <span>{exp.location}</span>
                          </div>
                        </div>

                        <ul className="space-y-2 mt-4">
                          {exp.responsibilities.map((resp, idx) => (
                            <li
                              key={idx}
                              className="flex items-start gap-3 text-gray-300"
                            >
                              <span className="text-cyan-400 mt-1.5">▹</span>
                              <span>{resp}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;